import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '../types';
import GoogleSheetsApiService from '../services/googleSheetsApi';

interface RegistrationForm {
  name: string;
  email: string;
  stravaId: string;
  age: string;
  gender: string;
  expectedPace: string;
  activityProof: string;
  activityType: 'strava' | 'file' | 'wallet' | 'earn' | 'transfer' | 'upload' | '';
}

interface WingateInvitationalProps {
  user: User | null;
}

interface LogEntry {
  id: number;
  fullId: string;
  username: string;
  date: string;
  wingoMined: number;
  kmLogged: number;
  initiation: boolean;
  category: string;
  activityLink: string;
}

const WingateInvitational: React.FC<WingateInvitationalProps> = ({ user }) => {
  const navigate = useNavigate();
  const [isRegistering, setIsRegistering] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [gateUnlockCount, setGateUnlockCount] = useState(0);
  const [formData, setFormData] = useState<RegistrationForm>({
    name: '',
    email: '',
    stravaId: '',
    age: '',
    gender: '',
    expectedPace: '',
    activityProof: '',
    activityType: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    // Auto-show the 10+1 popup when page loads every time
    setShowModal(true);
    // Fetch gate unlock count
    fetchGateUnlockCount();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Handle registration logic
    console.log('Registration submitted:', formData);
    setIsRegistering(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClose = () => setShowModal(false);

  const fetchGateUnlockCount = async () => {
    try {
      const sheetsService = GoogleSheetsApiService.getInstance();
      const logData = await sheetsService.getLogData();
      
      const parsed = logData
        .filter(entry => entry.username && entry.username !== '')
        .filter((entry, index, self) => 
          index === self.findIndex(e => e.id === entry.id)
        );

      // Count unique users with Gate Unlock transactions
      const gateUnlockUsers = new Set(
        parsed
          .filter(entry => entry.category === 'Gate Unlock')
          .map(entry => entry.username)
      );
      
      setGateUnlockCount(gateUnlockUsers.size);
    } catch (err) {
      console.error('Failed to fetch gate unlock count:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="text-center pt-4">
        <a href="/obwi-results" className="text-sm sm:text-base text-wingo-600 hover:text-wingo-700 font-semibold underline">
          See OBWI Results
        </a>
      </div>
      {showModal && (
        <div className="popup-container fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="popup-content bg-white rounded-xl shadow-2xl px-4 sm:px-8 pt-6 sm:pt-8 pb-4 max-w-md w-full mx-4 relative border-2 border-wingo-600/30 ring-2 ring-wingo-600/10">
            <button 
              className="popup-close absolute top-3 sm:top-4 right-3 sm:right-4 text-gray-400 hover:text-gray-700 text-3xl sm:text-2xl font-bold focus:outline-none p-2"
              onClick={handleClose}
              aria-label="Close"
            >
              ×
            </button>
            <div className="popup-header text-center mb-4">
              <div className="flex flex-col items-center justify-center gap-0">
                <span className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-wingo-600 flex items-center gap-2 mb-0">
                  WINGO World
                </span>
                <span className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#E6C200] leading-none">
                  10+1 <span className="text-gray-800">Program</span>
                </span>
              </div>
            </div>
            <div className="popup-body text-gray-700 space-y-4">
              <p className="text-base sm:text-xl font-semibold text-center">10 <span className="text-[#E6C200] font-bold">W</span>INGO <span> +1 Initiation Wingo</span> unlocks:</p>
              <ul className="list-disc pl-6 space-y-1 text-base sm:text-lg">
                <li>320 Track Club membership</li>
                <li>The Old Balance Wingate Invitational</li>
                <li><span className="font-bold">Coach D<span className="text-cyan-500 font-bold">AI</span>SY™</span> Wingo Workouts</li>
                <li>32% off Old Balance merch</li>
                <li>1 complimentary Winego</li>
                <li>1-Year subscription to Haystack</li>
              </ul>
            </div>
            <div className="popup-cta mt-3 flex flex-col items-center justify-center gap-2">
              <a 
                href="sms:9299254744" 
                className="bg-gray-900 hover:bg-gray-800 text-white font-bold py-1.5 sm:py-1.5 px-6 sm:px-6 rounded-full shadow-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 text-lg sm:text-lg text-center no-underline w-auto"
                style={{ display: 'inline-block' }}
              >
                Text D<span className="text-cyan-500 font-bold">AI</span>SY™ to Enroll
              </a>
              <div className="text-center text-lg mt-1 mb-0" style={{ fontSize: '1.25rem' }}>
                🐎🤖🪽8️⃣
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Hero Section */}
      <div className="relative bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="relative z-10 pb-8 bg-white sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
            <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
              <div className="sm:text-center lg:text-left">
                <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                  <span className="block text-gray-800">Old Balance</span>
                  <span className="block text-wingo-600 mt-2">Wingate Invitational</span>
                </h1>
                <div className="mt-6 mb-8">
                  <div className="w-16 h-px bg-gray-300 mx-auto lg:mx-0"></div>
                </div>
                <div className="space-y-3">
                  <p className="text-base sm:text-xl text-gray-600 font-medium">
                    Sunday, September 7, 2025 at 3:20 PM WT
                  </p>
                  <p className="text-base sm:text-lg text-gray-500 italic">
                    Not a mile. Not sold out. Update: Sold out (event has passed). 
                  </p>
                  <p className="text-sm text-gray-600 mt-4 font-medium">
                    Sponsored by <a href="/old-balance" className="text-wingo-600 hover:text-wingo-700 font-bold underline decoration-wingo-600 decoration-2 underline-offset-2">Old Balance</a>.
                  </p>
                </div>
              </div>
            </main>
          </div>
          {/* Old Balance Logo - Moved to right side */}
          <div className="hidden lg:block lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
            <a href="/old-balance">
              <img
                className="h-full w-full object-cover"
                src="/old_balance.png"
                alt="Old Balance"
                style={{ objectPosition: 'center 30%' }}
              />
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 py-12">
        {/* Mobile Old Balance Logo */}
        <div className="lg:hidden mb-8">
          <a href="/old-balance">
            <img
              className="w-full h-64 object-cover object-center"
              src="/old_balance.png"
              alt="Old Balance"
              style={{ objectPosition: 'center 20%' }}
            />
          </a>
        </div>
        <div className="grid grid-cols-1 gap-8">
          {/* Event Details */}
          <div className="space-y-8">
            <section className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 transform hover:scale-[1.02] transition-transform duration-200">
              <h2 className="text-3xl font-bold mb-6 text-wingo-600">Wingate Invitational</h2>
              <div className="space-y-4 text-gray-700">
                <p className="text-lg">
                  The <span className="font-bold">Old Balance Wingate Invitational</span> is the flagship experience of the 320 Track Club. It is not a m*le and it is not sold out.
                </p>
                <p className="text-lg">
                  <a href="sms:9299254744" className="text-wingo-600 hover:text-wingo-700 font-bold">Message Coach DAISY™</a> (929-WAK-GRIG) to experience the OBWI.
                </p>
                <p className="text-lg">
                  Registration: <span className="text-[#E6C200] font-bold">W</span> 10 + 1 initiation Wingo.
                </p>
                <p className="text-lg">
                  All <span className="inline-flex items-center">
                    <span className="text-[#E6C200] font-bold">W</span>
                    <span>INGO</span>
                  </span> will be reallocated to OBWI winners. Additional <span className="inline-flex items-center">
                    <span className="text-[#E6C200] font-bold">W</span>
                    <span>INGO</span>
                  </span> may be gained or lost through the <a href="/wager" className="text-wingo-600 hover:text-wingo-700">WINGO Wager Market</a>.
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Note: OBWI crowns the <span className="font-bold italic">right</span> times, not the fastest. Stay tuned.
                </p>
              </div>
            </section>

            {/* Location Section */}
            <section className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 transform hover:scale-[1.02] transition-transform duration-200">
              <h2 className="text-3xl font-bold mb-6 text-wingo-600">Location</h2>
              <div className="aspect-w-16 aspect-h-9">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.681823832913!2d-73.94705262370852!3d40.658941671401536!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c25b64fb0f1bf1%3A0xec3b714e74e97824!2sWingate%20Park!5e0!3m2!1sen!2sus!4v1746618184061!5m2!1sen!2sus&zoom=19&maptype=satellite&center=40.658941671401536,-73.94705262370852"
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-lg shadow-lg"
                />
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WingateInvitational; 